import { Title } from '@solidjs/meta'
import { type Component, createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js'
import { z } from 'zod'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import Container from '~/components/Container'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import { useAuth } from '~/contexts/AuthContext'
import { useAppState } from '~/contexts/ThemeContext'
import ThemeToggle from '~/layers/ThemeToggle'
import { useNavigate } from '~/router'
import api from '~/services/api'
import { authService } from '~/services/auth.service'
import type { OAuthProviderType } from '~/types/OAuth'
import { resolveError } from '~/utils/error'

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: { client_id: string; scope: string; callback: (response: { access_token?: string; error?: string }) => void }) => {
            requestAccessToken: (config?: { prompt?: string }) => void
          }
        }
      }
    }
  }
}

const loginSchema = z.object({
  email: z.string().email('Correo electrónico inválido'),
  password: z.string().min(1, 'La contraseña es requerida')
})

const LoginPage: Component = () => {
  const navigate = useNavigate()
  const { isOnline } = useAppState()
  const auth = useAuth()

  const [error, setError] = createSignal('')
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const [gsiLoaded, setGsiLoaded] = createSignal(false)
  const [oauthProviders, setOauthProviders] = createSignal<OAuthProviderType[]>([])
  const [oauthLoading, setOauthLoading] = createSignal(true)
  let tokenClient: { requestAccessToken: (config?: { prompt?: string }) => void } | null = null

  const provider = (key: string) => oauthProviders().find(p => p.provider === key)

  /** Carga el SDK de Google Identity Services solo cuando el proveedor está activo. */
  const loadGsi = (clientId: string) => {
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => {
      if (window.google?.accounts?.oauth2) {
        tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: 'openid email profile',
          callback: async response => {
            if (response.error) {
              if (response.error !== 'popup_closed' && response.error !== 'user_cancelled') {
                setError('Error al iniciar sesión con Google')
              }
              setIsSubmitting(false)
              return
            }
            if (response.access_token) {
              setError('')
              setIsSubmitting(true)
              try {
                const success = await auth.googleLogin(response.access_token)
                if (success) {
                  navigate('/')
                }
              } catch (err: unknown) {
                setError(resolveError(err))
              } finally {
                setIsSubmitting(false)
              }
            }
          }
        })
        setGsiLoaded(true)
      }
    }
    document.head.appendChild(script)
  }

  onMount(() => {
    authService
      .getOAuthConfig()
      .then(conf => {
        setOauthProviders(conf.providers)
        const google = conf.providers.find(p => p.provider === 'google')
        if (google?.enabled && google.clientId) {
          loadGsi(google.clientId)
        }
      })
      .catch(() => setOauthProviders([]))
      .finally(() => setOauthLoading(false))
  })

  onCleanup(() => {
    const script = document.querySelector('script[src*="accounts.google.com/gsi/client"]')
    if (script) script.remove()
  })

  const handleGoogleClick = () => {
    if (tokenClient) {
      tokenClient.requestAccessToken({ prompt: 'select_account' })
    }
  }

  const handleGithubClick = () => {
    const base = api.defaults.baseURL ?? 'http://localhost:3002/api'
    window.location.href = `${base}/auth/oauth/github/authorize`
  }

  const handleSubmit = async (formData: FormData) => {
    setError('')
    setIsSubmitting(true)

    try {
      const email = formData.get('email') as string
      const password = formData.get('password') as string

      const success = await auth.login(email, password)

      if (success) {
        navigate('/admin')
      }
    } catch (err: unknown) {
      setError(resolveError(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const google = createMemo(() => provider('google'))
  const github = createMemo(() => provider('github'))
  const hasOAuth = createMemo(() => oauthProviders().some(p => p.enabled))

  return (
    <>
      <Title>Iniciar Sesión | Florez Jaramillo</Title>
      <div class="h-[100dvh] w-full bg-background overflow-y-auto">
        <Container class="min-h-full py-8 flex items-center">
          <div class="w-full max-w-md mx-auto">
            <Card class="p-6">
              <div class="absolute top-4 right-4">
                <ThemeToggle />
              </div>

              <div class="text-center mb-8">
                <div class="w-24 h-24 bg-primary-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <Icon icon="material-symbols-light:inventory-2-outline" size={64} class="text-white" />
                </div>
                <h1 class="text-2xl font-bold text-foreground mb-2">Bienvenido de nuevo</h1>
                <p class="text-foreground-subtle">Inicia sesión para continuar</p>
              </div>

              <Show when={error()}>
                <div class="mb-6 p-3 bg-danger-soft border-danger-soft text-on-danger-soft rounded-lg">{error()}</div>
              </Show>

              <Form onSubmit={handleSubmit} validator={loginSchema}>
                <div class="space-y-6">
                  <Input name="email" type="email" label="Correo electrónico" placeholder="correo@ejemplo.com" required autocomplete="email" endIcon="material-symbols-light:mail-outline" />

                  <Input name="password" type="password" label="Contraseña" placeholder="••••••••" required autocomplete="current-password" />

                  <div class="flex items-center justify-between">
                    <div class="flex items-center">
                      <Checkbox id="remember-me" label="Recuérdame" name="rememberMe" />
                    </div>

                    <Button variant="text" color="default" onClick={() => navigate('/recuperar')} class="text-sm">
                      ¿Olvidaste tu contraseña?
                    </Button>
                  </div>

                  <Show
                    when={isOnline()}
                    fallback={
                      <Button prependIcon={'mdi:access-point-network-off'} fullWidth size="md" disabled>
                        Sin conexión
                      </Button>
                    }
                  >
                    <Button type="submit" class="flex justify-center" fullWidth disabled={isSubmitting()}>
                      {isSubmitting() ? (
                        <>
                          <div class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-border border-t-foreground"></div>
                          Procesando...
                        </>
                      ) : (
                        'Iniciar Sesión'
                      )}
                    </Button>
                  </Show>

                  <Show when={hasOAuth()}>
                    <div class="relative my-6">
                      <div class="absolute inset-0 flex items-center">
                        <div class="w-full border-t border-border-strong"></div>
                      </div>
                      <div class="relative flex justify-center text-sm">
                        <span class="px-2 bg-surface text-foreground">O continúa con</span>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 gap-3">
                      <Show when={google()?.enabled}>
                        <Button
                          onClick={handleGoogleClick}
                          variant="outlined"
                          color="default"
                          size="lg"
                          fullWidth
                          disabled={!gsiLoaded() || isSubmitting() || oauthLoading()}
                          class="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          <div class="flex items-center justify-center gap-3">
                            <Show
                              when={!gsiLoaded()}
                              fallback={
                                <svg viewBox="0 0 24 24" class="h-5 w-5 shrink-0" aria-hidden="true">
                                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                </svg>
                              }
                            >
                              <div class="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-slate-300 dark:border-slate-600 border-t-primary-500" />
                            </Show>
                            <span class="font-semibold">{isSubmitting() ? 'Conectando con Google...' : gsiLoaded() ? 'Continuar con Google' : 'Cargando Google...'}</span>
                          </div>
                        </Button>
                      </Show>

                      <Show when={github()?.enabled}>
                        <Button
                          onClick={handleGithubClick}
                          variant="outlined"
                          color="default"
                          size="lg"
                          fullWidth
                          disabled={oauthLoading()}
                          class="bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          <div class="flex items-center justify-center gap-3">
                            <svg viewBox="0 0 24 24" class="h-5 w-5 shrink-0 fill-slate-700 dark:fill-slate-200" aria-hidden="true">
                              <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                            </svg>
                            <span class="font-semibold">Continuar con GitHub</span>
                          </div>
                        </Button>
                      </Show>
                    </div>
                  </Show>
                </div>
              </Form>
            </Card>
          </div>
        </Container>
      </div>
    </>
  )
}

export default LoginPage
